<script>
import { format, parseISO } from 'date-fns';

export default {
  name: 'UnavailabilityCard',
  props: {
    unavailability: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      isHovered: false,
    };
  },
  computed: {
    staffName() {
      return this.unavailability.staff.name;
    },
    staffId() {
      return this.unavailability.staff.id;
    },
    branchName() {
      return this.unavailability.branch?.name || 'Global';
    },
    unavailableDate() {
      return this.unavailability.unavailable_date
        ? format(parseISO(this.unavailability.unavailable_date), 'dd MMMM yyyy')
        : '-';
    },
    startTime() {
      try {
        return format(
          parseISO(`${this.unavailability.unavailable_date}T${this.unavailability.start_time}`),
          'hh:mm a'
        );
      } catch {
        return this.unavailability.start_time;
      }
    },
    endTime() {
      try {
        return format(
          parseISO(`${this.unavailability.unavailable_date}T${this.unavailability.end_time}`),
          'hh:mm a'
        );
      } catch {
        return this.unavailability.end_time;
      }
    },
    timeRange() {
      if (!this.startTime && !this.endTime) return 'All day';
      if (!this.startTime) return `Until ${this.endTime}`;
      if (!this.endTime) return `From ${this.startTime}`;
      return `${this.startTime} - ${this.endTime}`;
    },
    reason() {
      return this.unavailability.reason || '-';
    },
    cardClasses() {
      return {
        'animate-pulse': this.loading,
        'opacity-70 cursor-not-allowed pointer-events-none': this.loading,
      };
    },
  },
  methods: {
    onEdit(event) {
      event.stopPropagation();
      this.$emit('edit', this.unavailability);
    },
    onDelete(event) {
      event.stopPropagation();
      this.$emit('delete', this.unavailability.id);
    },
    onMouseEnter() {
      if (!this.loading) this.isHovered = true;
    },
    onMouseLeave() {
      this.isHovered = false;
    },
  },
};
</script>

<template>
  <div
    class="relative bg-white dark:bg-slate-800 dark:text-white p-5 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 transition-all duration-200"
    :class="cardClasses"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10"
    >
      <fluent-icon icon="spinner" class="animate-spin h-6 w-6 text-primary" />
    </div>

    <!-- Hover Actions -->
    <transition name="fade">
      <div
        v-if="isHovered && !loading && showActions"
        class="absolute top-3 right-3 flex gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg p-1 z-20"
      >
        <button @click.stop="onEdit" class="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-600">
          <fluent-icon icon="edit" class="h-4 w-4" />
        </button>
        <button @click.stop="onDelete" class="p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900 text-red-600 dark:text-red-400">
          <fluent-icon icon="delete" class="h-4 w-4" />
        </button>
      </div>
    </transition>

    <!-- Content -->
    <div class="flex flex-col gap-1">
      <!-- <h4 class="text-lg font-semibold text-slate-800 dark:text-white truncate">
        {{ staffName }}
      </h4> -->
      <p class="text-sm text-slate-600 dark:text-slate-300">
        <strong>Branch:</strong> {{ branchName }}
      </p>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        <strong>Date:</strong> {{ unavailableDate }}
      </p>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        <strong>Time:</strong> {{ timeRange }}
      </p>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        <strong>Reason:</strong> {{ reason }}
      </p>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
